<script setup lang="ts">
import { vAutoAnimate } from "@formkit/auto-animate/vue";

const { t } = useI18n();

const notification = useGlobalNotification();

const messageList = reactive({
  [$ck.utils.NotificationType.Pending]: t("notifications.statusPending"),
  [$ck.utils.NotificationType.Success]: t("notifications.statusSuccess"),
  [$ck.utils.NotificationType.Error]: t("notifications.statusError"),
  [$ck.utils.NotificationType.Warning]: t("notifications.statusWarning"),
  /* Info notification won't be triggered if there's no message, but TS still wants this. */
  [$ck.utils.NotificationType.Info]: "",
});

function handleClose(item: NotificationMessage) {
  notification.clearMessage(item);
}

function handleHide(item: NotificationMessage) {
  notification.hideMessage(item);
}

function handleClearAll() {
  notification.clearAll();
}

const activeNotifications = computed(() =>
  notification.notificationQueue.reduce((acc, el) => {
    if (el.active) {
      acc.push(el);
    }
    return acc;
  }, [] as NotificationMessage[]),
);
</script>

<template>
  <TransitionGroup name="notification" tag="div">
    <div
      v-for="item in activeNotifications"
      :key="item.id"
      v-show-popover
      class="notification"
      :data-testid="`notification-${item.type}`"
      :class="item.type"
    >
      <div class="flex w-full items-baseline content-baseline justify-center">
        <base-icon
          v-if="item.type === $ck.utils.NotificationType.Pending"
          name="fa6-solid:spinner"
          size="1em"
          class="animate-spin font-black m-[5px]"
        />
        <div class="notification-copy">
          <p class="message">{{ item.message !== "" ? $t(item.message) : messageList[item.type] }}</p>
        </div>
        <p v-if="item.number > 1" class="notification-number">({{ item.number }})</p>
        <button
          v-if="item.type === $ck.utils.NotificationType.Error && !item.persist"
          class="notification-button"
          :aria-label="$t('buttons.close')"
          @click="handleClose(item)"
        >
          <base-icon name="mdi:window-close" size="1.25em" />
        </button>
        <button
          v-if="item.persist"
          class="notification-button"
          :aria-label="$t('buttons.hide')"
          @click="handleHide(item)"
        >
          <base-icon name="mdi:hide-outline" size="1em" />
        </button>
        <button class="clear-all-button" @click="handleClearAll">
          {{ $t("buttons.clearAll") }}
          <base-icon name="lucide:trash-2" size="1.25em" class="ml-1" />
        </button>
      </div>
      <div v-auto-animate class="flex items-center justify-center">
        <ProgressBar v-if="item.showProgress && notification.currentActive?.id === item.id" :value="item.progress" />
      </div>
    </div>
  </TransitionGroup>
</template>
<style scoped lang="postcss">
.notification {
  @apply flex flex-col items-center p-2 gap-2 shadow-xl;
  justify-content: center;
  z-index: 10000;
  position: fixed;
  inset-block-start: calc(3rem);
  inset-inline: 50%;
  translate: -50%;
  width: max(52vw, 325px);
  border-radius: 0.5rem;
  transition:
    inset-block-start 0.3s,
    max-height 0.3s;
  max-height: 52px;
  overflow-y: hidden;
}

.notification[popover="manual"] {
  inset-block-end: auto;
}

.notification-button {
  @apply: z-50 h-full flex flex-col justify-center;
  position: absolute;
  inset-block-start: 0.75rem;
  inset-inline-end: 1rem;
}

.clear-all-button {
  @apply: z-50 w-full;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: absolute;
  inset-block-end: 0.5rem;
  inset-inline-end: 0.5rem;
}

.message {
  max-height: 50px;
  padding-inline: 3ch;
  padding-block: 0.5rem;
  overflow-y: hidden;

  transition: max-height 0.3s;

  .notification:nth-child(1) &,
  .notification-leave-active ~ .notification:nth-child(2) & {
    max-height: 180px;
  }
}

.notification-copy {
  max-width: calc(100% - 4rem);
  padding-inline: 2rem;
  padding-block: 1rem;
}

.notification:nth-child(1) {
  inset-block-start: calc(3rem - 32px);
  z-index: 10006;
  max-height: 200px;
  overflow-y: initial;
}

.notification:nth-child(2) {
  inset-block-start: calc(3rem - 19px);
  z-index: 10005;

  .notification-leave-active ~ & {
    inset-block-start: calc(3rem - 32px);
    max-height: 200px;
    overflow-y: initial;
  }
}

.notification:nth-child(3) {
  inset-block-start: calc(3rem - 11px);
  z-index: 10004;

  .notification-leave-active ~ & {
    inset-block-start: calc(3rem - 19px);
  }
}

.notification:nth-child(4) {
  inset-block-start: calc(3rem - 6px);
  z-index: 10003;

  .notification-leave-active ~ & {
    inset-block-start: calc(3rem - 11px);
  }
}

.notification:nth-child(5) {
  inset-block-start: calc(3rem - 3px);
  z-index: 10002;

  .notification-leave-active ~ & {
    inset-block-start: calc(3rem - 6px);
  }
}

.notification:nth-child(6) {
  inset-block-start: calc(3rem - 1px);
  z-index: 10001;

  .notification-leave-active ~ & {
    inset-block-start: calc(3rem - 3px);
  }
}

.notification-leave-active ~ .notification:nth-child(7) {
  inset-block-start: calc(3rem - 1px);
}

.success {
  @apply bg-ngreen-1;
  @apply text-white;
}

.pending {
  @apply bg-nyellow-2;
  @apply text-ngray-1;
}

.warning {
  @apply bg-nyellow-2;
  @apply text-ngray-1;
}

.error {
  @apply bg-nred-5;
  @apply text-white;
}

.notification-enter-from,
.notification-leave-to {
  opacity: 0;
  filter: blur(4px);
  transform: translateY(-50px);
}

.notification-move,
.notification-enter-active,
.notification-leave-active {
  transition:
    inset-block-start 0.3s,
    max-height 0.3s,
    opacity 0.3s,
    transform 0.3s,
    filter 0.3s;
}
</style>
